// src/main/shell/symbolGrid/search/searchCommands.ts
// The SYMBOL instance of the shared tree-search engine. The generic parser /
// matcher / tokenizer now live in @vitesse/base-ui/search (shared with the
// log view); this file is just the symbol command spec + the symbol free-text
// matcher, plus thin wrappers that bind the engine to `FlatSymbol` so every
// existing call site (SymbolGrid, SymbolTreeView, SearchAutocomplete) keeps its
// exact signature. Single source of truth — change the grammar in the engine and
// both the symbol tree and the log tree get it.

import type { FlatSymbol } from '../types';
import * as engine from '@vitesse/base-ui/search';

// Re-exported types, bound to the symbol item + context, so consumers import the
// same names as before (`ParsedSearch`, `FilterContext`, …).
export type SearchCommand = engine.SearchCommand<FlatSymbol, FilterContext>;
export type ParsedCommand = engine.ParsedCommand<FlatSymbol, FilterContext>;
export type FilterGroup = engine.FilterGroup<FlatSymbol, FilterContext>;
export type ParsedSearch = engine.ParsedSearch<FlatSymbol, FilterContext>;
export type FormattedToken = engine.FormattedToken;

/**
 * Filter context — the only thing the command engine needs from the host is a
 * label lookup, supplied as a closure (decoupled from the SymbolManager).
 */
export interface FilterContext {
  getLabel: (symbolId: string) => string | undefined;
}

// Command definitions — the symbol columns as search commands. `descKey` is an
// i18n key resolved via t() at display time.
export const SEARCH_COMMANDS: SearchCommand[] = [
  {
    name: 'type',
    descKey: 'search.type',
    valueType: 'enum',
    enumValues: ['boolean', 'integer', 'number', 'string', 'object', 'array'],
    placeholder: '=boolean',
    match: (symbol, value) => {
      if (!value) return true;
      return symbol.type.toLowerCase() === value.toLowerCase();
    },
    suggestValue: (symbol) => symbol.type,
  },
  {
    name: 'value',
    descKey: 'search.value',
    valueType: 'string',
    placeholder: '=123',
    match: (symbol, value) => {
      if (!value) return true;
      const symbolValue = symbol.value;
      if (symbolValue === undefined || symbolValue === null) return false;
      return String(symbolValue).toLowerCase().includes(value.toLowerCase());
    },
    suggestValue: (symbol) =>
      symbol.value !== undefined && symbol.value !== null ? String(symbol.value) : undefined,
  },
  {
    name: 'label',
    descKey: 'search.label',
    valueType: 'string',
    placeholder: '=motor',
    match: (symbol, value, ctx) => {
      if (!value) return true;
      const label = ctx.getLabel(symbol.symbol_id);
      if (!label) return false;
      return label.toLowerCase().includes(value.toLowerCase());
    },
    suggestValue: (symbol) => symbol.label,
  },
  {
    name: 'perm',
    descKey: 'search.perm',
    valueType: 'enum',
    enumValues: ['r', 'rw', 'w'],
    placeholder: '=rw',
    match: (symbol, value) => {
      if (!value) return true;
      return symbol.permission.toLowerCase() === value.toLowerCase();
    },
    suggestValue: (symbol) => symbol.permission,
  },
  {
    name: 'name',
    descKey: 'search.name',
    valueType: 'string',
    placeholder: '=motor',
    match: (symbol, value) => {
      if (!value) return true;
      return symbol.symbol_id.toLowerCase().includes(value.toLowerCase());
    },
    suggestValue: (symbol) => symbol.symbol_id,
  },
];

/** Symbol free-text match: id / type / label (the engine delegates the bare-word
 *  query to this). Identical to the previous inline behavior. */
export function symbolTextMatch(symbol: FlatSymbol, query: string, ctx: FilterContext): boolean {
  const f = query.toLowerCase();
  const label = ctx.getLabel(symbol.symbol_id);
  return (
    symbol.symbol_id.toLowerCase().includes(f) ||
    symbol.type.toLowerCase().includes(f) ||
    (!!label && label.toLowerCase().includes(f))
  );
}

// ── Thin wrappers binding the generic engine to the symbol command set, so the
// existing call sites keep their exact (input-only) signatures. ──────────────

export function getCommand(name: string): SearchCommand | undefined {
  return engine.getCommand(name, SEARCH_COMMANDS);
}

export function getMatchingCommands(partial: string): SearchCommand[] {
  return engine.getMatchingCommands(partial, SEARCH_COMMANDS);
}

export function parseSearchInput(input: string): ParsedSearch {
  return engine.parseSearchInput(input, SEARCH_COMMANDS);
}

export function matchesFilters(symbol: FlatSymbol, parsed: ParsedSearch, ctx: FilterContext): boolean {
  return engine.matchesFilters(symbol, parsed, ctx, symbolTextMatch);
}

export function formatSearchInput(input: string): FormattedToken[] {
  return engine.formatSearchInput(input, SEARCH_COMMANDS);
}
